import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { saveTokens } from '../contexts/AuthContext';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cca8665b-5293-4dd5-89e0-2957c9d7e36c/artifacts/zjtkx4ss_LOGO-MAYAPLUS.png";

export default function VerifyCodePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = (params.get('email') || '').toLowerCase();
  const { checkAuth } = useAuth();
  const [digits, setDigits] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sent, setSent] = useState(false);
  const inputsRef = useRef([]);
  const sentRef = useRef(false);

  useEffect(() => { if (!email) navigate('/', { replace: true }); }, [email, navigate]);

  const sendCode = async () => {
    try {
      await axios.post(`${API}/auth/otp/send`, { email });
      setSent(true);
      setCooldown(30);
    } catch (e) {
      setError(e.response?.data?.detail || "Impossible d'envoyer le code");
    }
  };

  useEffect(() => {
    if (!email || sentRef.current) return;
    sentRef.current = true;
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async (code) => {
    setVerifying(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/otp/verify`, { email, code }, { withCredentials: true });
      saveTokens(data);
      await checkAuth();
      navigate(data.has_access ? '/' : '/signup/plans', { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || 'Code incorrect');
      setDigits(['', '', '', '']);
      inputsRef.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const onDigit = (i, val) => {
    const v = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 3) inputsRef.current[i + 1]?.focus();
    const code = next.join('');
    if (code.length === 4 && next.every(d => d !== '')) verify(code);
  };

  const onKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) inputsRef.current[i - 1]?.focus();
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 4);
    if (text.length === 4) {
      e.preventDefault();
      setDigits(text.split(''));
      verify(text);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-black text-white" style={{ fontFamily: "'Manrope', 'Avenir', Arial, sans-serif", background: 'linear-gradient(180deg, #1a0a12 0%, #0a0508 60%, #000 100%)' }} data-testid="verify-code-page">
      <header className="flex items-center justify-between px-6 md:px-14 pt-5 pb-4 border-b border-white/10">
        <img src={LOGO_URL} alt="Maya+" className="h-10 md:h-12 cursor-pointer" onClick={() => navigate('/')} />
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <h1 className="text-3xl md:text-4xl font-extrabold leading-tight mb-8" data-testid="verify-code-title">
            Entrez le code que nous avons envoyé à votre adresse e-mail
          </h1>

          <div className="flex items-center justify-between bg-white/10 rounded-md px-4 py-3.5 mb-8" data-testid="verify-code-email-box">
            <span className="text-white/90 truncate">{email}</span>
            <button onClick={() => navigate('/')} className="text-sm font-semibold underline hover:text-white text-white/80 ml-3 flex-none" data-testid="verify-code-edit-btn">Modifier</button>
          </div>

          <div className="flex gap-3 mb-5" onPaste={onPaste}>
            {digits.map((d, i) => (
              <input key={i} ref={el => (inputsRef.current[i] = el)} value={d}
                onChange={e => onDigit(i, e.target.value)} onKeyDown={e => onKeyDown(i, e)}
                inputMode="numeric" maxLength={2} autoFocus={i === 0} disabled={verifying}
                data-testid={`verify-code-digit-${i}`}
                className="w-16 h-16 md:w-[70px] md:h-[70px] bg-white/5 border border-white/30 rounded-md text-center text-2xl font-bold focus:outline-none focus:border-[#E9641A] transition-colors" />
            ))}
          </div>

          {error && <p className="text-sm text-red-400 mb-4" data-testid="verify-code-error">{error}</p>}
          {verifying && <p className="text-sm text-white/60 mb-4">Vérification…</p>}

          <p className="text-sm text-white/60">Ce code expirera dans 15 minutes.</p>
          <p className="text-sm text-white/60 mt-1">
            Vous n'avez pas reçu de code ?{' '}
            {cooldown > 0 ? (
              <span className="text-white/40">Renvoyer le code ({cooldown}s)</span>
            ) : (
              <button onClick={sendCode} className="underline hover:text-white font-semibold" data-testid="verify-code-resend-btn">Renvoyer le code.</button>
            )}
          </p>
          {sent && <p className="text-xs text-white/35 mt-4">Un code vous a été envoyé par e-mail.</p>}
        </div>
      </main>

      <Footer />
    </div>
  );
}
